import type { ActivityLogicPlugin } from '../../core/types/plugin.base.types';
import type { DesarrolloPayload, DesarrolloRuntime } from '../../core/types/payloads/desarrollo.types';
import { applyStimulusFields } from '../../core/print/blockStimulus';
import type { RoutingMode } from '../../core/types/room.types';
import type { HostActionPayload, StudentInputEnvelope } from '../../core/types/events.types';

export function validateDesarrolloPayload(raw: unknown): DesarrolloPayload {
  if (!raw || typeof raw !== 'object') throw new Error('Desarrollo payload must be an object');
  const data = raw as Partial<DesarrolloPayload>;
  if (!data.title) throw new Error('Desarrollo requires title');
  if (!data.prompt) throw new Error('Desarrollo requires prompt');
  const rubric = Array.isArray(data.rubric) ? data.rubric.map(String).filter(Boolean) : [];
  if (rubric.length < 1) throw new Error('Desarrollo requires at least one rubric criterion');
  const sourceText = data.sourceText != null ? String(data.sourceText).trim() : undefined;
  const printLayout =
    data.printLayout === 'math_workspace' || data.printLayout === 'writing_lines'
      ? data.printLayout
      : undefined;
  const blueprintPurpose =
    data.blueprintPurpose != null ? String(data.blueprintPurpose).trim() : undefined;
  return applyStimulusFields(
    {
      title: String(data.title),
      prompt: String(data.prompt),
      sourceText: sourceText || undefined,
      rubric,
      minChars: data.minChars != null ? Number(data.minChars) : 40,
      maxChars: data.maxChars != null ? Number(data.maxChars) : 1200,
      ...(printLayout ? { printLayout } : {}),
      ...(blueprintPurpose ? { blueprintPurpose } : {}),
    },
    raw,
    'desarrollo',
  );
}

export function createInitialDesarrolloRuntime(_payload: DesarrolloPayload, _routing: RoutingMode): DesarrolloRuntime {
  return { responses: {}, aiReviews: {}, aggregates: { submitted: 0 } };
}

/** Stub IA — reemplazar por llamada LLM en backend */
export function stubAiReview(text: string, rubric: string[]): { score: number; feedback: string } {
  const len = text.trim().length;
  const base = Math.min(10, Math.max(3, Math.floor(len / 40)));
  const hits = rubric.filter((r) => text.toLowerCase().includes(r.split(' ')[0]!.toLowerCase())).length;
  const score = Math.min(10, base + hits * 2);
  return {
    score,
    feedback: `Propuesta automática: ${hits}/${rubric.length} criterios detectados. Revisa y valida.`,
  };
}

export const desarrolloLogic: ActivityLogicPlugin<'desarrollo'> = {
  mode: 'desarrollo',
  validatePayload: validateDesarrolloPayload,
  createInitialRuntime: createInitialDesarrolloRuntime,

  onStudentInput(ctx, input: StudentInputEnvelope, participant) {
    if (input.action === 'DRAFT') {
      const data = input.data as { text: string };
      const responses = {
        ...ctx.runtime.responses,
        [participant.participantId]: { text: String(data.text ?? '') },
      };
      return { runtime: { ...ctx.runtime, responses } };
    }
    if (input.action === 'SUBMIT') {
      const entry = ctx.runtime.responses[participant.participantId];
      const text = entry?.text?.trim() ?? '';
      if (text.length < (ctx.payload.minChars ?? 0)) {
        return { runtime: ctx.runtime, rejected: true, rejectReason: 'TOO_SHORT' };
      }
      const ai = stubAiReview(text, ctx.payload.rubric);
      const aiReviews = {
        ...ctx.runtime.aiReviews,
        [participant.participantId]: { ...ai, pending: true },
      };
      const submitted = Object.keys({ ...ctx.runtime.responses, [participant.participantId]: entry }).length;
      return {
        runtime: {
          ...ctx.runtime,
          responses: {
            ...ctx.runtime.responses,
            [participant.participantId]: { text, submittedAt: Date.now() },
          },
          aiReviews,
          aggregates: { submitted },
        },
      };
    }
    return { runtime: ctx.runtime, rejected: true, rejectReason: 'UNKNOWN_ACTION' };
  },

  onHostAction(ctx, action: HostActionPayload) {
    if (action.action === 'CONFIRM_AI_SCORE') {
      const data = action.data as { participantId: string };
      const review = ctx.runtime.aiReviews[data.participantId];
      if (!review) return { runtime: ctx.runtime };
      const aiReviews = {
        ...ctx.runtime.aiReviews,
        [data.participantId]: { ...review, pending: false },
      };
      return {
        runtime: { ...ctx.runtime, aiReviews },
        scores: [{ participantId: data.participantId, delta: review.score, reason: 'desarrollo_ai' }],
      };
    }
    return { runtime: ctx.runtime };
  },
};

export function demoDesarrolloPayload(): DesarrolloPayload {
  // Neutro — no Revolución Industrial (fuga cross-subject vía defaultPayloadForMode).
  return validateDesarrolloPayload({
    title: 'Explica con tus palabras',
    prompt: 'Explica un aspecto central del tema con al menos dos ideas claras.',
    rubric: ['Menciona dos ideas', 'Usa vocabulario del tema', 'Organiza en párrafos'],
  });
}
